import { useContext, useEffect, useState } from 'react';
import {
  ActionDependant,
  evaluateTemplateValue,
  GlobalDataContext,
  TemplateContext,
} from '@ea-lab/reactive-json';
import { ChevronRight, Home } from 'lucide-react';

async function fetchDirectory(id) {
  const res = await fetch(
    `/decoupled-api/taxonomy_term/doc_directory/${id}?display=decoupled`,
    { credentials: 'include' },
  );
  if (!res.ok) return null;
  const json = await res.json();
  const d = json?.data ?? json;
  return {
    id: d.id,
    name: d.name || '',
    icon: d.icon || '',
    parentId: d.parentId ? parseInt(d.parentId, 10) : 0,
  };
}

export const DocBreadcrumbs = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const [crumbs, setCrumbs] = useState([]);

  const directoryId = evaluateTemplateValue({
    valueToEvaluate: props.directoryId,
    globalDataContext,
    templateContext,
  });

  const pageTitle = evaluateTemplateValue({
    valueToEvaluate: props.pageTitle,
    globalDataContext,
    templateContext,
  });

  useEffect(() => {
    if (!directoryId) return;

    let cancelled = false;
    const chain = [];

    const walk = async (id) => {
      const dir = await fetchDirectory(id);
      if (cancelled || !dir) return;
      chain.unshift(dir);
      if (dir.parentId && dir.parentId !== 0) {
        await walk(dir.parentId);
      }
    };

    walk(directoryId).then(() => {
      if (!cancelled) setCrumbs(chain);
    });

    return () => { cancelled = true; };
  }, [directoryId]);

  return (
    <ActionDependant {...props}>
      <nav className="flex items-center gap-1.5 text-sm flex-wrap mb-4" aria-label="Fil d'Ariane">
        <a
          href="/docs"
          className="text-stone-500 hover:text-blue-600 hover:underline transition-colors cursor-pointer flex items-center gap-1"
        >
          <Home size={13} />
          <span>Documentation</span>
        </a>

        {crumbs.map((dir, idx) => (
          <span key={dir.id} className="flex items-center gap-1.5">
            <ChevronRight size={12} className="text-stone-300" />
            {idx === 0 ? (
              <a
                href={`/docs/space/${dir.id}`}
                className="text-stone-500 hover:text-blue-600 hover:underline transition-colors cursor-pointer"
              >
                {dir.name}
              </a>
            ) : (
              <span className="text-stone-400">
                {dir.name}
              </span>
            )}
          </span>
        ))}

        {pageTitle && (
          <span className="flex items-center gap-1.5">
            <ChevronRight size={12} className="text-stone-300" />
            <span className="text-stone-700 font-medium truncate max-w-xs">
              {pageTitle}
            </span>
          </span>
        )}
      </nav>
    </ActionDependant>
  );
};
